<script lang="ts" setup>
import type { IconName } from '~ds/lib/icons';
import type { InputProps, TextInputTypes } from '~kui/types/input';

import { useTextareaAutosize } from '@vueuse/core';
import { Icon } from '~ds/components/ui/icon';
import { useCharacterCount, useInputState } from '~kui/composables/useInputState';

type InputVariant = 'primary' | 'secondary';

interface Props extends InputProps {
  type?: TextInputTypes;
  id?: string;
  placeholder?: string | undefined;
  errorMessages?: string[];
  disabled?: boolean;
  readOnly?: boolean;
  autocomplete?: string;
  dataTestId?: string;
  buttonType?: IconName;
  buttonLabel?: string;
  autoGrow?: boolean;
  maxLength?: number;
  variant?: InputVariant;
  hasBorder?: boolean;
  name?: string;
}

const props = withDefaults(defineProps<Props>(), {
  label: undefined,
  type: 'text',
  errorMessages: () => [],
  placeholder: undefined,
  autocomplete: undefined,
  hideErrorMessage: false,
  dataTestId: undefined,
  buttonType: undefined,
  buttonLabel: undefined,
  autoGrow: false,
  maxLength: 300,
  variant: 'primary',
  hasBorder: false,
  id: undefined,
});

const emit = defineEmits<{
  blur: [event: FocusEvent];
  handleButtonClick: [];
}>();

const value = defineModel<string | number>({ required: false });

const { getInputStateClasses } = useInputState({
  disabled: computed(() => props.disabled),
  errorMessages: computed(() => props.errorMessages),
  value,
  variant: props.variant,
});

const stringValue = computed(() => (typeof value.value === 'string' ? value.value : ''));
const { enforceCharacterLimit } = useCharacterCount(stringValue, props.maxLength);

const handleCharacterLimit = () => {
  if (typeof value.value === 'string' && value.value.length > props.maxLength) {
    value.value = enforceCharacterLimit(value.value);
  }
};

const variantClasses = computed<string>(() => {
  return twMerge(
    getInputStateClasses('w-full bg-white font-normal shadow-md').value,
    cva('', {
      variants: {
        variant: {
          primary: 'placeholder:text-muted-foreground rounded-2xl p-4 text-base transition-all duration-200',
          secondary: 'rounded-4xl border-none p-3 text-lg shadow-none placeholder:text-foreground',
        },
        autoGrow: {
          true: 'block resize-none',
          false: '',
        },
        showButton: {
          true: 'pr-13',
          false: '',
        },
      },
      defaultVariants: {
        variant: 'primary',
        autoGrow: false,
      },
    })({
      variant: props.variant,
      autoGrow: props.autoGrow,
      showButton: !!props.buttonType,
    }),
  );
});

const buttonClasses = computed(() => {
  return twMerge(
    cva('absolute top-1/2 flex -translate-y-1/2 transform items-center justify-center rounded-full', {
      variants: {
        variant: {
          primary: 'right-4 size-8',
          secondary: 'right-4 size-10',
        },
        disabled: {
          true: 'bg-muted',
          false: 'bg-secondary',
        },
      },
      defaultVariants: {
        variant: 'primary',
        disabled: false,
      },
    })({
      variant: props.variant,
      disabled: props.disabled,
    }),
  );
});

const borderVariantClasses = computed(() => {
  return twMerge(
    cva('', {
      variants: {
        hasBorder: {
          true: 'rounded-4xl border-input border p-2',
          false: '',
        },
      },
    })({
      hasBorder: props.hasBorder,
    }),
  );
});

const inputRef = ref<HTMLInputElement | HTMLTextAreaElement | null>(null);

const { textarea: autoGrowTextarea, input: autoGrowInput } = useTextareaAutosize({
  styleProp: 'minHeight',
});

watch(
  () => value.value,
  () => {
    if (props.autoGrow && autoGrowInput.value !== undefined) {
      autoGrowInput.value = value.value?.toString() || '';
    }
  },
);

const focus = () => {
  if (props.autoGrow) {
    autoGrowTextarea.value?.focus();
  }
  else {
    inputRef.value?.focus();
  }
};

watch(
  () => value.value,
  () => {
    handleCharacterLimit();
  },
);

defineExpose({
  focus,
});

const elementId = useId();

const hasError = computed(() => props.errorMessages.length > 0);
const showErrorMessages = computed(() => !props.hideErrorMessage && hasError.value);
const errorsId = computed(() => `error-${elementId}`);
</script>

<template>
  <div>
    <KUICoreInputLabel
      v-if="!hideLabel && label"
      :label="label"
      :for="id ?? elementId"
      :required="required"
    />
    <div :class="borderVariantClasses" class="relative">
      <textarea
        v-if="autoGrow"
        :id="id ? id : elementId"
        ref="autoGrowTextarea"
        v-model="value"
        :aria-required="required"
        :aria-invalid="hasError ? 'true' : undefined"
        :aria-describedby="showErrorMessages ? errorsId : undefined"
        :class="variantClasses"
        :data-testid="dataTestId"
        :name="name"
        :disabled="disabled"
        :placeholder="placeholder"
        :readonly="Boolean(readOnly)"
        rows="1"
        @blur="emit('blur', $event)"
      />
      <KUICoreInput
        v-else
        :id="id ? id : elementId"
        ref="inputRef"
        v-model="value"
        :aria-required="required"
        :aria-invalid="hasError ? 'true' : undefined"
        :aria-describedby="showErrorMessages ? errorsId : undefined"
        :autocomplete="autocomplete"
        :class="variantClasses"
        :name="name"
        :data-testid="dataTestId"
        :disabled="disabled"
        :placeholder="placeholder"
        :readonly="readOnly"
        :type="type"
        @blur="emit('blur', $event)"
      />
      <KUICoreClickable
        v-if="buttonType"
        :data-testid="`${dataTestId}-button`"
        :aria-label="buttonLabel"
        :disabled="disabled"
        :class="buttonClasses"
        @click="emit('handleButtonClick')"
      >
        <Icon size="sm" :name="buttonType" />
      </KUICoreClickable>
    </div>
    <KUICoreInputErrors
      v-if="showErrorMessages"
      :id="errorsId"
      :data-testid="`error-message-${elementId}`"
      :error-messages="errorMessages"
      :font-size="errorMessageSize"
    />
  </div>
</template>
